/** Injected clock and timers, so tests run deterministically and the engine never relies on module globals. */
export interface Scheduler {
  now(): number;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const systemScheduler: Scheduler = {
  now: () => Date.now(),
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: handle => globalThis.clearTimeout(handle as Parameters<typeof globalThis.clearTimeout>[0]),
};

/** Named timers owned by one object; `clearAll` leaves nothing scheduled. */
export class TimerSet {
  private readonly handles = new Map<string, unknown>();
  constructor(private readonly scheduler: Scheduler) {}

  set(name: string, ms: number, callback: () => void): void {
    this.clear(name);
    const handle = this.scheduler.setTimeout(() => { this.handles.delete(name); callback(); }, Math.max(0, ms));
    this.handles.set(name, handle);
  }

  has(name: string): boolean { return this.handles.has(name); }

  clear(name: string): void {
    const handle = this.handles.get(name);
    if (handle !== undefined) { this.scheduler.clearTimeout(handle); this.handles.delete(name); }
  }

  clearAll(): void {
    for (const handle of this.handles.values()) this.scheduler.clearTimeout(handle);
    this.handles.clear();
  }

  get size(): number { return this.handles.size; }
}

/** UX spec 7.1: 1, 2, 4, 8, 15 and 30 s with +/-20% jitter. */
export const DEFAULT_RECONNECT_DELAYS_MS: readonly number[] = Object.freeze([1000, 2000, 4000, 8000, 15000, 30000]);

export function backoffDelay(attempt: number, schedule: readonly number[], jitter: number, random: () => number): number {
  const index = Math.min(Math.max(attempt, 1), schedule.length) - 1;
  const base = schedule[index] ?? 30000;
  const spread = Math.min(Math.max(jitter, 0), 0.5);
  return Math.max(0, Math.round(base * (1 + spread * (random() * 2 - 1))));
}

/** Resolves with `fallback` if `promise` has not settled within `ms`. Never rejects because of the timer. */
export function withTimeout<T, F>(scheduler: Scheduler, promise: Promise<T>, ms: number, fallback: F): Promise<T | F> {
  return new Promise<T | F>((resolve, reject) => {
    let settled = false;
    const handle = scheduler.setTimeout(() => { if (!settled) { settled = true; resolve(fallback); } }, ms);
    promise.then(
      value => { if (!settled) { settled = true; scheduler.clearTimeout(handle); resolve(value); } },
      error => { if (!settled) { settled = true; scheduler.clearTimeout(handle); reject(error); } },
    );
  });
}

export function delay(scheduler: Scheduler, ms: number): Promise<void> {
  return new Promise(resolve => { scheduler.setTimeout(resolve, ms); });
}
